import { Chip } from '@/components/chip/Chip';
import { Icon } from '@/components/icon/Icon';
import { QuantityDataCell } from '@/lib/_utils/tables';
import { getInfoByOrderCloseMode, orderTypeView } from '@/lib/_utils';
import { ProfitLossView } from '@/lib/_components/ProfitLossView';
import { createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { formatOrderMode } from '../_utils';
import { MyOrderAction } from '../_utils/interfaces';
import { Translate } from 'next-translate';

const columnHelper = createColumnHelper();

export const OPEN_AT = (t: Translate) => {
	return columnHelper.accessor('open_at', {
		id: 'my-order-open-at',
		header: t('common:date_time'),
		cell: (data) => {
			return (
				<div>
					<div>{data.getValue() ? dayjs(data.getValue()).format('MM/DD/YYYY - HH:mm:ss') : '-'}</div>
				</div>
			);
		},
	});
};

export const CREATED_AT = (t: Translate) => {
	return columnHelper.accessor('created_at', {
		id: 'my-order-created-at',
		header: t('common:created_at'),
		cell: (data) => <div>{data.getValue() ? dayjs(data.getValue()).format('MM/DD/YYYY - HH:mm:ss') : '-'}</div>,
	});
};

export const CLOSED_AT = (t: Translate) => {
	return columnHelper.accessor('closed_at', {
		id: 'my-order-close-at',
		header: t('common:closed_at'),
		cell: (data) => {
			return <div>{data.getValue() ? dayjs(data.getValue()).format('MM/DD/YYYY - HH:mm:ss') : '-'}</div>;
		},
	});
};

export const TYPE = (t: Translate) => {
	return columnHelper.accessor('type', {
		id: 'my-order-type',
		header: t('common:open_mode'),
		size: 0,
		cell: (data) => {
			// @ts-expect-error @ragonebrainin should check this
			const open_mode = data?.row?.original.open_mode;
			const type = orderTypeView(data.getValue()).label;

			return (
				<div>
					<Chip variant={orderTypeView(data.getValue()).variant} label={formatOrderMode(type, open_mode)} />
				</div>
			);
		},
	});
};

export const TICKER_LABEL = (t: Translate) => {
	return columnHelper.accessor('ticker_label', {
		id: 'my-order-ticker',
		header: t('common:symbol'),
		cell: (data) => <p>{data.getValue()}</p>,
	});
};

export const OPEN_PRICE = (t: Translate) => {
	return columnHelper.accessor('open_price', {
		id: 'my-order-open-price',
		header: t('common:open_price'),
		cell: (data) => {
			const value = data.getValue() as number;
			// @ts-expect-error @ragonebrainin @rown89 should check this
			const decimal_precision = data?.row?.original?.ticker_decimal_precision || 0;
			return <p>{value ? value.toFixed(decimal_precision) : '-'}</p>;
		},
	});
};

export const CURRENT_PRICE = (t: Translate) => {
	return columnHelper.accessor('current_price', {
		id: 'my-current-price',
		header: t('common:current_price'),
		cell: (data) => {
			const value = data.getValue() as number;
			// @ts-expect-error @ragonebrainin @rown89 should check this
			const decimal_precision = data?.row?.original?.ticker_decimal_precision || 0;
			return <p>{value ? value.toFixed(decimal_precision) : '-'}</p>;
		},
	});
};

export const PENDING_OPEN_PRICE = (t: Translate) => {
	return columnHelper.accessor('pending_open_price', {
		id: 'my-order-pending-open-price',
		header: t('common:open_price'),
		cell: (data) => {
			const value = data.getValue() as number;
			// @ts-expect-error @ragonebrainin @rown89 should check this
			const decimal_precision = data?.row?.original?.ticker_decimal_precision || 0;
			return <p>{value ? value.toFixed(decimal_precision) : '-'}</p>;
		},
	});
};

export const QUANTIY = (t: Translate) => {
	return columnHelper.accessor('quantity', {
		id: 'my-order-quantity',
		header: t('common:quantity'),
		size: 0,
		cell: (data) => <QuantityDataCell quantity={data.getValue()} />,
	});
};

export const SL_PRICE = (t: Translate) => {
	return columnHelper.accessor('sl_price', {
		id: 'my-order-sl-price',
		header: t('common:s/l'),
		cell: (data) => {
			const value = data.getValue() as number;
			// @ts-expect-error @ragonebrainin @rown89 should check this
			const decimal_precision = data?.row?.original?.ticker_decimal_precision || 0;
			return <p>{value ? value.toFixed(decimal_precision) : '-'}</p>;
		},
	});
};

export const TP_PRICE = (t: Translate) => {
	return columnHelper.accessor('tp_price', {
		id: 'my-order-tp-price',
		header: t('common:t/p'),
		cell: (data) => {
			const value = data.getValue() as number;
			// @ts-expect-error @ragonebrainin @rown89 should check this
			const decimal_precision = data?.row?.original?.ticker_decimal_precision || 0;
			return <p>{value ? value.toFixed(decimal_precision) : '-'}</p>;
		},
	});
};

export const CLOSED_MODE = (t: Translate) => {
	return columnHelper.accessor('closed_mode', {
		id: 'my-order-closed-mode',
		header: t('common:close_mode'),
		cell: (data) => <p className='uppercase'>{getInfoByOrderCloseMode(data.getValue()).label}</p>,
	});
};

export const CLOSED_PRICE = (t: Translate) => {
	return columnHelper.accessor('closed_price', {
		id: 'my-order-closed-price',
		header: t('common:closed_price'),
		cell: (data) => {
			return <div>{data.getValue()}</div>;
		},
	});
};

export const PROFIT_LOSS = (t: Translate) => {
	return columnHelper.accessor('pl', {
		id: 'my-order-pl',
		header: t('common:pl_%'),
		cell: (data) => {
			// @ts-expect-error @ragonebrainin @rown89 should check this
			const decimal_precision = data?.row?.original?.ticker_decimal_precision || 0;
			return <ProfitLossView value={data.getValue() ?? 0} decimal_precision={decimal_precision || 0} />;
		},
	});
};

export const EDIT_DELETE = (handleOrderModal: ({ id, action }: MyOrderAction) => void) => {
	return columnHelper.accessor('edit-delete', {
		id: 'my-order-edit-delete',
		header: '',
		size: 0,
		cell: (data) => {
			// @ts-expect-error @ragonebrainin @rown89 should check this
			const id = Number(data.row.original?.id);

			return (
				<div className='flex justify-center gap-3 hover:cursor-pointer'>
					{data.getValue()}
					<button className={'btn-transition transition-none'} onClick={() => handleOrderModal({ id, action: 'edit' })}>
						<Icon name='pencilIcon' />
					</button>

					<button
						className={'btn-transition transition-none'}
						onClick={() => handleOrderModal({ id, action: 'close' })}>
						<Icon name='crossIcon' />
					</button>
				</div>
			);
		},
	});
};

export const SELECT_SYMBOL = (t: Translate, selectSymbol: ({ id, action }: MyOrderAction) => void) => {
	return columnHelper.accessor('ticker', {
		id: `my-order-select-symbol-${Math.random()}`,
		header: t('common:order_id'),
		size: 0,
		cell: (data) => {
			// @ts-expect-error @ragonebrainin @rown89 should check this
			const id = Number(data.row.original?.id);
			return (
				<div
					className='btn-transition flex transition-none hover:cursor-pointer'
					onClick={() => selectSymbol({ id: data.getValue(), action: 'select_symbol' })}>
					<div>
						<Icon name='reportIcon' />
					</div>
					<div className='ml-lg'>ID{id}</div>
				</div>
			);
		},
	});
};
